/**
 * The engineer's terminal.
 *
 * Everything that decides anything is somewhere else. The endpoint holds the contract and drops
 * frames its capabilities do not permit; the WASM layer refuses to send before the Noise handshake
 * completes. This component draws a terminal and moves bytes, and it is deliberately incapable of
 * granting itself anything.
 *
 * # A view-only session is view-only because the endpoint drops the keystrokes
 *
 * Not because this component hides the cursor. It does disable input when the contract grants no
 * `input_control`, because a terminal that swallows typing without saying why is a support ticket
 * — but that is a courtesy, not the control. Anybody can write their own client; the endpoint is
 * what stops them.
 *
 * # Why the terminal is not a controlled React component
 *
 * xterm.js owns a canvas and its own scrollback. Re-rendering it from React state would mean
 * re-drawing a terminal on every keystroke, which is both slow and wrong — the terminal IS the
 * state. React owns the frame around it and nothing inside it.
 */

import { useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';

import { FitAddon } from '@xterm/addon-fit';
import { Terminal } from '@xterm/xterm';
import '@xterm/xterm/css/xterm.css';

import {
  openSession,
  type PresentedTicket,
  type ServedContract,
  type Session,
} from '../shared/session/client';
import { ErrorBox } from '../shared/ui/ui';
import './session-console.css';

/** What the caller must have obtained from the verified contract and the access request. */
export interface ConsoleProps {
  /** `wss://` URL of the relay. */
  relayUrl: string;
  /** The single-use rendezvous token for this session. */
  rendezvousToken: Uint8Array;
  /**
   * The client-signed contract, passed through to the WASM layer unread.
   *
   * This component neither parses it nor learns the endpoint's key from it — `openSession` hands
   * it to `verifyContract`, which checks the client's signature and produces the only value
   * `startHandshake` accepts (ADR-0020). Nothing in this file could substitute a key if it wanted
   * to, which is a better guarantee than a comment asking it not to.
   */
  contract: ServedContract;
  /**
   * The ticket and the identity that proves it, when the platform issued one (ADR-0021).
   *
   * `null` leaves the rendezvous a bearer token, which is what a deployment mid-rollout uses and
   * what the relay's open mode accepts. Passed straight through — this component does not read it.
   */
  ticket: PresentedTicket | null;
  /** Whether the contract grants input. False renders a read-only terminal. */
  mayType: boolean;
  /** Called when the session ends, so the surrounding screen can navigate away. */
  onEnded: (reason: string) => void;
}

/** Where the session is in its life. */
type Phase = 'connecting' | 'verifying' | 'live' | 'ended';

export function SessionConsole(props: ConsoleProps): JSX.Element {
  const { relayUrl, rendezvousToken, contract, ticket, mayType, onEnded } = props;

  const mount = useRef<HTMLDivElement>(null);
  // A ref rather than a local `let`: the flag is set from the cleanup closure, and TypeScript's
  // flow analysis narrows a local to `false` at every read — reporting the guards below as dead
  // code when they are the thing that stops a resolved promise touching an unmounted terminal.
  const disposed = useRef(false);
  const [phase, setPhase] = useState<Phase>('connecting');
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    const element = mount.current;
    if (!element) return;

    let session: Session | undefined;
    disposed.current = false;

    const terminal = new Terminal({
      convertEol: false,
      cursorBlink: mayType,
      disableStdin: !mayType,
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
      fontSize: 13,
      scrollback: 10_000,
      // The terminal renders whatever the target sends. A device's banner is attacker-influenced
      // in the threat model's sense, and xterm.js is the parser standing between it and the DOM —
      // which is why the escape sequences it does not understand are dropped rather than passed
      // through to innerHTML by anything here.
      allowProposedApi: false,
    });

    const fit = new FitAddon();
    terminal.loadAddon(fit);
    terminal.open(element);
    fit.fit();

    // Resizes are debounced by the browser's own observer callback batching; each one is bounded
    // and sent to the endpoint, which passes it to the PTY. A terminal at the wrong width wraps
    // every line wrongly, and the recording inherits that permanently.
    const observer = new ResizeObserver(() => {
      fit.fit();
      session?.resize(terminal.cols, terminal.rows);
    });
    observer.observe(element);

    void (async (): Promise<void> => {
      try {
        setPhase('verifying');
        const opened = await openSession(
          // `exactOptionalPropertyTypes` is on, so an optional property must be OMITTED rather
          // than set to undefined. That strictness is what makes "absent" and "present but
          // undefined" different types here, and the two mean different things in this codebase:
          // absent is a bearer rendezvous, which the relay's open mode accepts.
          ticket === null
            ? { relayUrl, rendezvousToken, contract }
            : { relayUrl, rendezvousToken, contract, ticket },
          {
            onOutput: (bytes) => {
              terminal.write(bytes);
            },
            onSecured: () => {
              if (!disposed.current) setPhase('live');
            },
            onClosed: (reason) => {
              if (disposed.current) return;
              setPhase('ended');
              terminal.writeln(`\r\n\x1b[2m── ${reason} ──\x1b[0m`);
              onEnded(reason);
            },
            onError: (err) => {
              if (!disposed.current) setError(err);
            },
          },
        );

        if (disposed.current) {
          opened.dispose();
          return;
        }

        session = opened;
        session.resize(terminal.cols, terminal.rows);

        if (mayType) {
          terminal.onData((data) => {
            session?.send(data);
          });
        }
      } catch (err) {
        if (!disposed.current) {
          setError(err);
          setPhase('ended');
        }
      }
    })();

    return (): void => {
      disposed.current = true;
      observer.disconnect();
      session?.dispose();
      terminal.dispose();
    };
    // The session is opened once per set of connection parameters. Re-running on a callback
    // identity change would tear down a live terminal mid-session.
  }, [relayUrl, rendezvousToken, contract, ticket, mayType, onEnded]);

  return (
    <div className="console">
      <div className="console__bar">
        <span className={`console__state console__state--${phase}`} role="status">
          {phase === 'connecting' && 'Connecting…'}
          {phase === 'verifying' && 'Verifying the endpoint…'}
          {phase === 'live' && (mayType ? 'Live · encrypted end to end' : 'Live · view only')}
          {phase === 'ended' && 'Ended'}
        </span>

        {phase === 'live' ? (
          <button
            className="button console__end"
            type="button"
            onClick={() => {
              onEnded('closed by the engineer');
            }}
          >
            End session
          </button>
        ) : null}
      </div>

      <ErrorBox error={error} />

      {/*
        The terminal mounts here and React does not touch it again. `aria-label` rather than inner
        text: the content is a live byte stream, and a screen reader announcing every frame would
        be unusable. xterm.js maintains its own accessibility tree for the buffer.
      */}
      <div className="console__terminal" ref={mount} aria-label="Remote session terminal" />
    </div>
  );
}
